/*
 * live-preview-companion.js — the «companion» card (snippets/live-preview-
 * companion.liquid): one product that belongs with the one just added, its
 * cover already carrying the customer's own words.
 *
 * What this file does, and in this order for every card it finds:
 *   1. derives the companion's text from the source item's descriptor — by
 *      RULE, never by guessing: names travel as they are, a line that is the
 *      source product's own title is dropped, a dedication to a mother is
 *      turned round for a father («Στην καλύτερη μαμά» → «Στον καλύτερο
 *      μπαμπά»), and a name nobody knows yet (the baby's) is left empty;
 *   2. picks the companion's colour — the source's, if the companion comes in
 *      it; else Snow (Stelios, 2026-09-22); else the first available — and
 *      offers the others as swatches;
 *   3. hands the text to the standalone <live-preview> inside the card, which
 *      fits it, records the lines and captures the picture exactly as the PDP
 *      does (assets/live-preview.js, config.standalone);
 *   4. on «add», posts ONE line item with the same properties a PDP add
 *      carries — the typed text, `_lp_preview`, `_preview_base`,
 *      `_custom_product`, `_preview_link`, `_lp_image` — so the print pipeline
 *      cannot tell the two apart; an empty field adds the plain product.
 *
 * Loaded GLOBALLY (layout/theme.liquid): the notification injects the card with
 * innerHTML, which runs no inline script, so the watcher has to be on the
 * page already. On a page that never loaded live-preview.js the card fetches
 * it itself (data-lp-script) before drawing.
 *
 * The rules are pure functions exported for node (tools/live-preview/
 * companion_rules_test.mjs) — the same file, no copy.
 *
 * ⚠ NOTHING HERE SENDS THE CUSTOMER'S TEXT ANYWHERE BUT THE CART. Every rule
 * runs locally. The card had a «✨ Φτιάξ' το…» button for one day: Stelios
 * removed it on 2026-09-24 («δεν εξυπηρετεί κάτι σε αυτό το σημείο») once the
 * rules learned to put the companion's name where the first book's name was.
 * The Worker's `companion` action is deployed and unused — it costs nothing
 * idle and is the way back if the card ever needs a model. The analytics carry
 * the pair, the place and what happened — never the words.
 */
(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root) root.LPCompanion = api;
  if (root && root.document) api.boot();
})(typeof window !== 'undefined' ? window : null, function () {
  'use strict';

  // ═══ 1. The rules ═════════════════════════════════════════════════════════

  // Accent-blind, case-blind, quote-blind form of a line, for the drop list.
  function norm(s) {
    return String(s == null ? '' : s)
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[«»"“”„'’‘`]/g, '')
      .replace(/[.,;:!?…]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  // A product's own title belongs to THAT cover, and it travels in two shapes.
  //
  // As a LINE of its own: «Ο γάμος μας» above two names is the wedding book's
  // title, not the couple's words — 26% of wedding orders write it themselves
  // (cover-text patterns, 2026-09-14). And INSIDE a line, which is what
  // Stelios found on the first preview (2026-09-22): «Στέλιος & Ευγενία
  // Wedding Planner» on the wedding book is right and on the honeymoon diary
  // is a different book's name, printed. Both shapes go; what is left is the
  // part that belongs to the people — «Στέλιος & Ευγενία».
  //
  // Only these known phrases, and the source product's own title, are removed.
  // Anything else the customer wrote is theirs and is never edited by a rule —
  // that is what the model is for (the ✨ button on the card).
  var DROP = [
    'ο γαμος μας', 'γαμος μας', 'our wedding', 'the wedding', 'wedding',
    'save the date', 'το ταξιδι του μελιτος', 'ταξιδι του μελιτος',
    'μηνας του μελιτος', 'honeymoon', 'our honeymoon',
    'σχολικο ημερολογιο', 'ημερολογιο συναισθηματων', 'το ημερολογιο μου',
    'ο μηνας του μελιτος', 'το ταξιδι μας',
    'wedding planner', 'wedding calendar', 'my wedding calendar',
    'wedding book', 'wedding diary', 'wedding journal',
    'ημερολογιο γαμου', 'το ημερολογιο του γαμου μας', 'ημερολογιο του γαμου',
    'honeymoon trip', 'the honeymoon trip', 'honeymoon diary', 'travel planner',
    'ημερολογιο εγκυμοσυνης', 'θα γινω μαμα', 'πες μου μαμα', 'πες μου μπαμπα',
    'πες μου γιαγια', 'πες μου παππου', 'το ημερολογιο του μωρου μου',
    'ημερολογιο μωρου', 'baby diary', 'school planner', 'moodbook',
  ];

  // The phrases as words, longest first, so «my wedding calendar» is tried
  // before «wedding calendar» and «wedding».
  function phraseList(extra) {
    var all = DROP.slice();
    (extra || []).forEach(function (x) { var n = norm(x); if (n && all.indexOf(n) === -1) all.push(n); });
    return all.sort(function (a, b) { return b.split(' ').length - a.split(' ').length || b.length - a.length; });
  }

  // Remove a phrase from a line by comparing WORDS in normalised form, so the
  // customer's accents, case and punctuation decide nothing and their own
  // spacing survives everywhere else.
  // Returns {text, cutAt}: what is left of the line, and WHERE the book's name
  // was — 'start', 'end', 'whole' or null. The position is what decides whether
  // the companion's own name can take its place (Stelios, 2026-09-24).
  function stripPhrases(line, phrases) {
    var raw = String(line == null ? '' : line);
    var toks = raw.split(/(\s+)/);
    var cut = false;
    var atStart = false, atEnd = false;
    var words = [];
    for (var i = 0; i < toks.length; i++) if (toks[i] && !/^\s+$/.test(toks[i])) words.push(i);
    var total = words.length;
    for (var p = 0; p < phrases.length; p++) {
      var want = phrases[p].split(' ');
      for (var w = 0; w + want.length <= words.length; w++) {
        var hit = true;
        for (var k = 0; k < want.length; k++) {
          if (norm(toks[words[w + k]]) !== want[k]) { hit = false; break; }
        }
        if (!hit) continue;
        if (w === 0) atStart = true;
        if (w + want.length === words.length) atEnd = true;
        for (var k2 = 0; k2 < want.length; k2++) toks[words[w + k2]] = '';
        words.splice(w, want.length);
        cut = true;
        w--;
      }
    }
    // Nothing removed: the line is the customer's, spacing and all. Only the
    // gap a removal leaves behind is closed.
    if (!cut) return { text: raw, cutAt: null };
    var left = toks.join('').replace(/\s+/g, ' ').trim();
    var where = !left ? 'whole' : atStart ? 'start' : atEnd ? 'end' : 'middle';
    // A name that sat in the middle of the customer's own words is not a
    // heading and nothing takes its place.
    return { text: left, cutAt: where === 'middle' ? null : where, had: total };
  }

  // `cover` is the companion's own name, as it belongs on a cover. Where the
  // FIRST book's name opened or closed a line, the second book's name takes
  // that place — «Στέλιος & Ευγενία Wedding Planner» → «Στέλιος & Ευγενία
  // Ταξίδι του Μέλιτος», «Πες μου Μαμά ⏎ Στην καλύτερη μαμά…» → «Πες μου
  // Μπαμπά ⏎ Στον καλύτερο μπαμπά…». Once per text: a name that appears twice
  // is a name printed twice.
  function dropTitleLines(text, extra, cover) {
    var phrases = phraseList(extra);
    var lines = String(text || '').split('\n');
    var name = String(cover || '').trim();
    // Already in the customer's words (however they spelled it)? Then nothing
    // is put back — they wrote it themselves.
    var already = name && norm(String(text || '')).indexOf(norm(name)) !== -1;
    var used = false;
    var kept = [];
    for (var i = 0; i < lines.length; i++) {
      var hadText = lines[i].trim();
      // A line that is ALREADY the companion's own name stays: either the
      // customer wrote it, or the role swap just produced it («Πες μου Μαμά»
      // → «Πες μου Μπαμπά»). Stripping it as a «known title» would throw away
      // the very name that belongs on this cover.
      if (name && hadText && norm(hadText) === norm(name)) {
        kept.push(lines[i]);
        used = true;
        continue;
      }
      var r = stripPhrases(lines[i], phrases);
      var line = r.text;
      if (name && !used && !already && r.cutAt) {
        if (r.cutAt === 'whole') { line = name; used = true; }
        else if (r.cutAt === 'start') { line = name + ' ' + line; used = true; }
        else if (r.cutAt === 'end') { line = line + ' ' + name; used = true; }
      }
      // A line that WAS something and is now nothing was a title line with
      // nothing to put in its place: it goes altogether. A line that was blank
      // stays blank (the customer's spacing).
      if (hadText && !line) continue;
      kept.push(hadText ? line : lines[i]);
    }
    while (kept.length && !kept[0].trim()) kept.shift();
    while (kept.length && !kept[kept.length - 1].trim()) kept.pop();
    return kept.join('\n');
  }

  // ── Turning a dedication round ────────────────────────────────────────────
  //
  // The words that name the person, in every form the orders use, and the
  // article + adjective that agree with them. Only those two agree: «του
  // κόσμου» after «μπαμπά» is a different «του» and must not move, so the
  // article and adjectives are swapped ONLY when they stand directly before
  // the role word. Anything not in the tables is left exactly as typed —
  // the customer edits, or the helper (on their tap) rewrites.
  //
  // Case of the role word follows the article before it: «η/Η» is nominative
  // (→ μπαμπάς, παππούς), «του/της» genitive (→ γιαγιάς, μαμάς, where the
  // target declines), everything else accusative (→ μπαμπά, παππού) — «μαμά»
  // and «γιαγιά» do not decline in the accusative, so the source never says.
  var ROLE = {
    'mom-dad': {
      // role word → { acc: accusative/genitive form, nom: nominative form }
      words: {
        'μαμα': { acc: 'μπαμπά', nom: 'μπαμπάς' },
        'μαμακα': { acc: 'μπαμπάκα', nom: 'μπαμπάκας' },
        'μανουλα': { acc: 'μπαμπάκα', nom: 'μπαμπάκας' },
        'μητερα': { acc: 'πατέρα', nom: 'πατέρας' },
        'μαμας': { acc: 'μπαμπά', nom: 'μπαμπά' },
      },
      articles: { 'στην': 'στον', 'στη': 'στον', 'την': 'τον', 'τη': 'τον', 'της': 'του', 'η': 'ο', 'μια': 'έναν', 'μία': 'έναν' },
      adjectives: {
        'καλυτερη': { acc: 'καλύτερο', nom: 'καλύτερος' },
        'αγαπημενη': { acc: 'αγαπημένο', nom: 'αγαπημένος' },
        'πολυαγαπημενη': { acc: 'πολυαγαπημένο', nom: 'πολυαγαπημένος' },
        'υπεροχη': { acc: 'υπέροχο', nom: 'υπέροχος' },
        'μοναδικη': { acc: 'μοναδικό', nom: 'μοναδικός' },
        'γλυκια': { acc: 'γλυκό', nom: 'γλυκός' },
        'ομορφη': { acc: 'όμορφο', nom: 'όμορφος' },
        'καλη': { acc: 'καλό', nom: 'καλός' },
        'δικη': { acc: 'δικό', nom: 'δικός' },
        'πρωτη': { acc: 'πρώτο', nom: 'πρώτος' },
        'μεγαλη': { acc: 'μεγάλο', nom: 'μεγάλος' },
        'πιο': { acc: 'πιο', nom: 'πιο' },
        'μου': { acc: 'μου', nom: 'μου' },
        'μας': { acc: 'μας', nom: 'μας' },
      },
    },
    'dad-mom': {
      words: {
        'μπαμπα': { acc: 'μαμά', nom: 'μαμά', gen: 'μαμάς' },
        'μπαμπας': { acc: 'μαμά', nom: 'μαμά' },
        'μπαμπακα': { acc: 'μαμάκα', nom: 'μαμάκα', gen: 'μαμάκας' },
        'μπαμπακας': { acc: 'μαμάκα', nom: 'μαμάκα' },
        'πατερα': { acc: 'μητέρα', nom: 'μητέρα', gen: 'μητέρας' },
        'πατερας': { acc: 'μητέρα', nom: 'μητέρα' },
      },
      articles: { 'στον': 'στην', 'τον': 'την', 'του': 'της', 'ο': 'η', 'εναν': 'μια', 'ενα': 'μια' },
      adjectives: {
        'καλυτερο': { acc: 'καλύτερη', nom: 'καλύτερη' },
        'καλυτερος': { acc: 'καλύτερη', nom: 'καλύτερη' },
        'αγαπημενο': { acc: 'αγαπημένη', nom: 'αγαπημένη' },
        'αγαπημενος': { acc: 'αγαπημένη', nom: 'αγαπημένη' },
        'πολυαγαπημενο': { acc: 'πολυαγαπημένη', nom: 'πολυαγαπημένη' },
        'πολυαγαπημενος': { acc: 'πολυαγαπημένη', nom: 'πολυαγαπημένη' },
        'υπεροχο': { acc: 'υπέροχη', nom: 'υπέροχη' },
        'υπεροχος': { acc: 'υπέροχη', nom: 'υπέροχη' },
        'μοναδικο': { acc: 'μοναδική', nom: 'μοναδική' },
        'μοναδικος': { acc: 'μοναδική', nom: 'μοναδική' },
        'γλυκο': { acc: 'γλυκιά', nom: 'γλυκιά' },
        'γλυκος': { acc: 'γλυκιά', nom: 'γλυκιά' },
        'ομορφο': { acc: 'όμορφη', nom: 'όμορφη' },
        'ομορφος': { acc: 'όμορφη', nom: 'όμορφη' },
        'καλο': { acc: 'καλή', nom: 'καλή' },
        'καλος': { acc: 'καλή', nom: 'καλή' },
        'δικο': { acc: 'δική', nom: 'δική' },
        'δικος': { acc: 'δική', nom: 'δική' },
        'πρωτο': { acc: 'πρώτη', nom: 'πρώτη' },
        'πρωτος': { acc: 'πρώτη', nom: 'πρώτη' },
        'μεγαλο': { acc: 'μεγάλη', nom: 'μεγάλη' },
        'μεγαλος': { acc: 'μεγάλη', nom: 'μεγάλη' },
        'πιο': { acc: 'πιο', nom: 'πιο' },
        'μου': { acc: 'μου', nom: 'μου' },
        'μας': { acc: 'μας', nom: 'μας' },
      },
    },
  };
  // The grandparents decline the same way as the parents — same articles and
  // adjectives, other nouns.
  ROLE['grandma-grandpa'] = {
    words: {
      'γιαγια': { acc: 'παππού', nom: 'παππούς' },
      'γιαγιας': { acc: 'παππού', nom: 'παππού' },
      'γιαγιακα': { acc: 'παππούλη', nom: 'παππούλης' },
      'γιαγιουλα': { acc: 'παππούλη', nom: 'παππούλης' },
    },
    articles: ROLE['mom-dad'].articles,
    adjectives: ROLE['mom-dad'].adjectives,
  };
  ROLE['grandpa-grandma'] = {
    words: {
      'παππου': { acc: 'γιαγιά', nom: 'γιαγιά', gen: 'γιαγιάς' },
      'παππους': { acc: 'γιαγιά', nom: 'γιαγιά' },
      'παππουλη': { acc: 'γιαγιάκα', nom: 'γιαγιάκα', gen: 'γιαγιάκας' },
      'παππουλης': { acc: 'γιαγιάκα', nom: 'γιαγιάκα' },
    },
    articles: ROLE['dad-mom'].articles,
    adjectives: ROLE['dad-mom'].adjectives,
  };
  // «η καλύτερη γιαγιά» → «ο καλύτερος παππούς»: nominative needs the
  // masculine -ος; the shared table already carries it as `nom`.

  // Split a token into leading punctuation, the word, trailing punctuation.
  var TOKEN = /^([^\p{L}\p{N}]*)(.*?)([^\p{L}\p{N}]*)$/u;

  function matchCase(src, out) {
    if (!src) return out;
    if (src === src.toLocaleUpperCase('el-GR') && src !== src.toLocaleLowerCase('el-GR')) {
      // ALL CAPS: Greek drops the tone in capitals, which is what the
      // browser's own uppercase does too (check_orders.py knows this).
      return out.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleUpperCase('el-GR');
    }
    var first = src.charAt(0);
    if (first !== first.toLocaleLowerCase('el-GR')) {
      return out.charAt(0).toLocaleUpperCase('el-GR') + out.slice(1);
    }
    return out;
  }

  // «στην/την» → «στη/τη» before a consonant that drops the ν. Only the
  // feminine forms are touched; anything else comes back as it was.
  var KEEPS_NU = /^(?:[αεηιουωάέήίόύώϊϋΐΰ]|κ|π|τ|ξ|ψ|γκ|μπ|ντ|τσ|τζ)/i;
  function finalNu(article, next) {
    var low = norm(article);
    if (low !== 'στην' && low !== 'την') return article;
    var n = norm(next);
    if (!n || KEEPS_NU.test(n)) return article;
    return article.slice(0, -1);
  }

  function swapRole(text, table) {
    if (!table) return text;
    var lines = String(text || '').split('\n');
    for (var L = 0; L < lines.length; L++) {
      var toks = lines[L].split(/(\s+)/); // keep the whitespace runs
      var parts = toks.map(function (t) {
        var m = TOKEN.exec(t) || ['', '', t, ''];
        return { pre: m[1], word: m[2], post: m[3], raw: t, key: norm(m[2]) };
      });
      for (var i = 0; i < parts.length; i++) {
        var p = parts[i];
        if (!p.word || !table.words[p.key]) continue;
        // Walk back over the adjectives that agree, then one article.
        var j = i - 1, adjs = [], article = -1;
        while (j >= 0) {
          var q = parts[j];
          if (!q.word) { j--; continue; } // a whitespace run
          if (table.adjectives[q.key]) { adjs.push(j); j--; continue; }
          if (table.articles[q.key]) article = j;
          break;
        }
        var ak = article !== -1 ? parts[article].key : '';
        var form = (ak === 'η' || ak === 'ο') ? 'nom' : (ak === 'του' || ak === 'της') ? 'gen' : 'acc';
        var entry = table.words[p.key];
        p.word = matchCase(p.word, entry[form] || entry.acc);
        p.key = '';
        for (var a = 0; a < adjs.length; a++) {
          var ad = parts[adjs[a]];
          var adj = table.adjectives[ad.key];
          ad.word = matchCase(ad.word, adj[form] || adj.acc);
          ad.key = '';
        }
        if (article !== -1) {
          var ar = parts[article];
          ar.word = matchCase(ar.word, table.articles[ar.key]);
          ar.key = '';
          // The feminine article keeps its final ν only before a vowel or
          // κ π τ ξ ψ (and the double consonants): «στην αγαπημένη μαμά» but
          // «στη μαμά», «στη γιαγιά». The masculine keeps it always.
          var nx = article + 1;
          while (nx < parts.length && !parts[nx].word) nx++;
          if (nx < parts.length) ar.word = finalNu(ar.word, parts[nx].word);
        }
      }
      lines[L] = parts.map(function (p) { return p.word || p.pre || p.post ? p.pre + p.word + p.post : p.raw; }).join('');
    }
    return lines.join('\n');
  }

  // rule → the companion's text. `titles` are extra phrases to strip (the
  // source product's own title, and the companion's). Returns '' when there is
  // nothing honest to prefill; the card then shows its placeholder.
  function deriveText(rule, sourceText, titles, cover) {
    var text = String(sourceText || '').replace(/\r\n?/g, '\n');
    switch (rule) {
      case 'empty': return '';
      case 'swap-mom-dad': return dropTitleLines(swapRole(text, ROLE['mom-dad']), titles, cover);
      case 'swap-dad-mom': return dropTitleLines(swapRole(text, ROLE['dad-mom']), titles, cover);
      case 'swap-grandma-grandpa': return dropTitleLines(swapRole(text, ROLE['grandma-grandpa']), titles, cover);
      case 'swap-grandpa-grandma': return dropTitleLines(swapRole(text, ROLE['grandpa-grandma']), titles, cover);
      case 'copy':
      default: return dropTitleLines(text, titles, cover);
    }
  }

  // The companion's colour: the source's if the companion comes in it, else
  // Snow, else the first available. Returns the variant object or null.
  function pickVariant(variants, sourceTitle) {
    var list = (variants || []).filter(function (v) { return v && v.available !== false; });
    if (!list.length) list = variants || [];
    if (!list.length) return null;
    var want = norm(sourceTitle);
    var same = null, snow = null;
    for (var i = 0; i < list.length; i++) {
      var t = norm(list[i].title);
      if (want && t === want && !same) same = list[i];
      if (t === 'snow' && !snow) snow = list[i];
    }
    var pick = same || snow || list[0];
    return { variant: pick, same: !!same };
  }

  // ═══ 2. The card ══════════════════════════════════════════════════════════

  var TYPED_FALLBACK = 'Γράψε το κείμενο που θέλεις να τυπώσουμε στο εξώφυλλο';
  function track(card, action, extra) {
    var params = { event: 'lp_companion', lp_action: action, lp_pair: card.getAttribute('data-lp-pair') || '', lp_place: card.getAttribute('data-lp-place') || '' };
    if (extra) for (var k in extra) if (Object.prototype.hasOwnProperty.call(extra, k)) params[k] = extra[k];
    try { window.dataLayer = window.dataLayer || []; window.dataLayer.push(params); } catch (e) { /* analytics never breaks the shop */ }
    try { if (typeof window.gtag === 'function') window.gtag('event', 'lp_companion', params); } catch (e) { /* same */ }
  }

  function parseJson(s, fallback) {
    try { return JSON.parse(s); } catch (e) { return fallback; }
  }

  function money(cents) {
    var n = Number(cents) / 100;
    try { return n.toLocaleString('el-GR', { style: 'currency', currency: 'EUR' }); } catch (e) { return n.toFixed(2) + ' €'; }
  }

  function status(card, text, kind) {
    var el = card.querySelector('[data-lp-companion-status]');
    if (!el) return;
    el.textContent = text || '';
    el.classList.toggle('is-error', kind === 'error');
    el.classList.toggle('is-ok', kind === 'ok');
  }

  // live-preview.js may not be on this page (a quick-add on a collection page,
  // say): fetch it once, then upgrade.
  var scriptPromise = null;
  function ensureElement(card) {
    if (!window.customElements) return Promise.resolve(); // nothing can upgrade; the card stays a picture
    if (customElements.get('live-preview')) return Promise.resolve();
    if (!scriptPromise) {
      scriptPromise = new Promise(function (res) {
        var src = card.getAttribute('data-lp-script');
        if (!src || document.querySelector('script[src="' + src + '"]')) {
          // Already requested (defer) — wait for the definition.
          customElements.whenDefined('live-preview').then(res, res);
          return;
        }
        var s = document.createElement('script');
        s.src = src;
        s.onload = function () { customElements.whenDefined('live-preview').then(res, res); };
        s.onerror = function () { res(); };
        document.head.appendChild(s);
      });
    }
    return scriptPromise;
  }

  // The field is as tall as the text needs, two rows at least and six at most.
  // Since the companion's own name goes on the first line (2026-09-24) three
  // lines are the common case, and a fixed two-row box cut the date off.
  function fitField(field) {
    if (!field) return;
    try {
      var line = parseFloat(getComputedStyle(field).lineHeight) || 18;
      var pad = field.offsetHeight - field.clientHeight + 14;
      field.style.height = 'auto';
      var want = Math.max(2 * line, Math.min(6 * line, field.scrollHeight));
      field.style.height = Math.round(want + pad) + 'px';
    } catch (e) { /* the CSS height stands */ }
  }

  function setupSwatches(card, variants, chosen, sameColour) {
    var host = card.querySelector('[data-lp-swatches]');
    var same = card.querySelector('[data-lp-same]');
    if (!host) return;
    host.innerHTML = '';
    if (variants.length > 1) {
      variants.forEach(function (v) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'lp-companion__swatch';
        b.setAttribute('role', 'radio');
        b.setAttribute('aria-checked', chosen && v.id === chosen.id ? 'true' : 'false');
        b.setAttribute('data-lp-swatch', String(v.id));
        if (v.available === false) b.disabled = true;
        if (v.thumb) {
          var im = document.createElement('img');
          im.src = v.thumb; im.alt = ''; im.width = 22; im.height = 22; im.loading = 'lazy';
          b.appendChild(im);
        }
        b.appendChild(document.createTextNode(v.title || ''));
        host.appendChild(b);
      });
    } else {
      host.hidden = true;
    }
    if (same) same.hidden = !sameColour;
  }

  function currentVariant(card) {
    var input = card.querySelector('[data-lp-variant]');
    return input ? Number(input.value) : null;
  }

  // Custom Cover Price (2026-10-20): a colour whose cover is paid carries its
  // hidden «Προσωποποιημένο» sibling (`custom`, snippets/live-preview-
  // companion.liquid). The card stays on the COLOUR — its artwork, its swatch,
  // the descriptor's variant — and adds the sibling whenever its field has
  // words, so the cart charges what the price line says.
  function entryFor(card, id) {
    var list = card.__lpVariants || [];
    for (var i = 0; i < list.length; i++) if (list[i] && list[i].id === id) return list[i];
    return null;
  }
  function hasWords(card) {
    var field = card.querySelector('[data-lp-input]');
    return !!(field && String(field.value || '').trim());
  }
  function variantToAdd(card) {
    var vid = currentVariant(card);
    var v = entryFor(card, vid);
    return v && v.custom && hasWords(card) ? Number(v.custom) : vid;
  }
  function showPrice(card) {
    var el = card.querySelector('[data-lp-companion-price]');
    var v = entryFor(card, currentVariant(card));
    if (!el || !v) return;
    var m = v.custom && hasWords(card) ? v.customMoney : v.money;
    if (m && el.textContent !== '+ ' + m) el.textContent = '+ ' + m;
  }

  function chooseVariant(card, v, byCustomer) {
    if (!v) return;
    var lp = card.querySelector('live-preview');
    var input = card.querySelector('[data-lp-variant]');
    if (lp && typeof lp.setVariant === 'function') lp.setVariant(v.id);
    else if (input) input.value = String(v.id);
    var photo = card.querySelector('[data-lp-photo]');
    if (photo && v.photo) photo.src = v.photo;
    card.querySelectorAll('[data-lp-swatch]').forEach(function (b) {
      b.setAttribute('aria-checked', Number(b.getAttribute('data-lp-swatch')) === v.id ? 'true' : 'false');
    });
    var same = card.querySelector('[data-lp-same]');
    if (same && byCustomer) same.hidden = norm(v.title) !== norm(card.getAttribute('data-lp-source-variant'));
    showPrice(card);
    if (byCustomer) track(card, 'variant');
  }

  // Wait, briefly, for the standalone preview to have written a descriptor for
  // a non-empty field. It renders on a 0 ms timer and re-fits when the face
  // lands; a customer who taps «add» inside that window would otherwise post
  // words with no descriptor — which the pipeline prints as a blank cover.
  function descriptorReady(lp, tries) {
    var input = lp && lp.querySelector('[data-lp-preview]');
    if (!input) return Promise.resolve(false);
    if (input.value) return Promise.resolve(true);
    if (tries <= 0) return Promise.resolve(false);
    return new Promise(function (res) { setTimeout(function () { descriptorReady(lp, tries - 1).then(res); }, 120); });
  }

  function sectionsFor(place) {
    // 'panel': a suggestion's own card inside the «Προστέθηκε» panel — the panel's sections, as the drawer's
    if (place === 'drawer' || place === 'panel') {
      var cd = document.querySelector('cart-drawer');
      if (cd && typeof cd.getSectionsToRender === 'function') {
        return cd.getSectionsToRender().map(function (s) { return s.id; });
      }
      return ['cart-drawer', 'cart-icon-bubble'];
    }
    if (place === 'cart') {
      var ci = document.querySelector('cart-items');
      if (ci && typeof ci.getSectionsToRender === 'function') {
        return ci.getSectionsToRender().map(function (s) { return s.section; });
      }
      return ['main-cart-items', 'main-cart-footer', 'cart-icon-bubble', 'cart-live-region-text'];
    }
    var cn = document.querySelector('cart-notification');
    if (cn && typeof cn.getSectionsToRender === 'function') {
      return cn.getSectionsToRender().map(function (s) { return s.id; });
    }
    return ['cart-notification-product', 'cart-notification-button', 'cart-icon-bubble'];
  }

  function paint(card, place, response) {
    if (place === 'drawer' || place === 'panel') {
      var cd = document.querySelector('cart-drawer');
      // The «Προστέθηκε στο καλάθι σου» panel (phase 2 of the Skroutz-style cart, assets/cart-drawer.js): in
      // place — the card keeps its «Προστέθηκε ✓», the free-shipping bar, the total and the bubble catch up,
      // and nothing else moves under the customer's finger.
      if (cd && typeof cd.panelAdded === 'function' && response.sections) {
        cd.panelAdded(response, card);
        return;
      }
      // Dawn's drawer re-rendered itself and stayed open on the new item; our card
      // is inside its `.js-contents`, so it is replaced by that render — and
      // the fresh one offers nothing, because the companion is now in the cart.
      if (cd && typeof cd.renderContents === 'function' && response.sections) cd.renderContents(response);
      else card.remove();
      return;
    }
    if (place === 'cart') {
      var ci = document.querySelector('cart-items');
      if (ci && typeof ci.getSectionsToRender === 'function' && response.sections) {
        ci.getSectionsToRender().forEach(function (section) {
          var host = document.getElementById(section.id);
          if (!host) return;
          var target = host.querySelector(section.selector) || host;
          var html = response.sections[section.section];
          if (!html) return;
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var src = doc.querySelector(section.selector);
          if (src) target.innerHTML = src.innerHTML;
        });
      }
      card.remove();
      return;
    }
    var cn = document.querySelector('cart-notification');
    if (cn && typeof cn.renderContents === 'function' && response.sections) {
      // The notification now shows the companion as the item just added; its
      // own card, offering the source back, is suppressed by the snippet
      // because the source is in the cart.
      cn.renderContents(response);
    } else {
      card.remove();
    }
  }

  function add(card) {
    var place = card.getAttribute('data-lp-place') || 'notification';
    var btn = card.querySelector('[data-lp-companion-add]');
    var lp = card.querySelector('live-preview');
    if (!currentVariant(card)) { status(card, 'Διάλεξε χρώμα.', 'error'); return Promise.resolve(false); }
    // the colour, or its paid-cover sibling when the field has words
    var vid = variantToAdd(card);
    // The field itself, not the element's copy of it: on a page where the
    // element never upgraded the customer can still have typed.
    var field = card.querySelector('[data-lp-input]');
    var text = field ? String(field.value || '') : '';
    var typedKey = card.getAttribute('data-lp-typed-key') || TYPED_FALLBACK;
    if (btn) { btn.setAttribute('aria-busy', 'true'); }
    status(card, '');

    var ready = text.trim() ? descriptorReady(lp, 8) : Promise.resolve(true);
    return ready.then(function (ok) {
      var body = new FormData();
      body.append('id', String(vid));
      body.append('quantity', '1');
      if (text.trim()) {
        var previewInput = lp.querySelector('[data-lp-preview]');
        var baseInput = lp.querySelector('[data-lp-preview-base]');
        var linkInput = lp.querySelector('[data-lp-link]');
        var imageInput = lp.querySelector('[data-lp-image]');
        if (!ok || !previewInput || !previewInput.value) {
          // Words with no preview behind them would print on a blank cover.
          // Refuse, and point at the product page, where the full preview is.
          var url = card.getAttribute('data-lp-product-url') || '#';
          status(card, 'Η προεπισκόπηση δεν ετοιμάστηκε — άνοιξε το προϊόν για να γράψεις το κείμενο εκεί: ' + url, 'error');
          track(card, 'refused', { lp_why: 'nodesc' });
          if (btn) btn.removeAttribute('aria-busy');
          return false;
        }
        // The last chance to stop a STALE picture travelling with the order —
        // the same seal the PDP applies on submit.
        try { if (typeof lp.sealCapture === 'function') lp.sealCapture(); } catch (e) { /* never break the add */ }
        body.append('properties[' + typedKey + ']', text);
        body.append('properties[_lp_preview]', previewInput.value);
        body.append('properties[_preview_base]', (baseInput && baseInput.value) || '');
        body.append('properties[_custom_product]', 'Yes');
        body.append('properties[_preview_link]', (linkInput && linkInput.value) || '');
        if (imageInput && imageInput.value) body.append('properties[_lp_image]', imageInput.value);
        // The customer's answer about the spelling (live-preview-assist.js,
        // 2026-10-12): the print queue prints a flagged word as typed only
        // with it. Refreshed first — time on screen counts up to this tap.
        try { if (typeof lp.lpAccentsRecord === 'function') lp.lpAccentsRecord(); } catch (e) { /* never break the add */ }
        var accentsInput = lp.querySelector('[data-lp-accents-record]');
        if (accentsInput && !accentsInput.disabled && accentsInput.value) body.append('properties[_lp_accents]', accentsInput.value);
      }
      var sections = sectionsFor(place);
      body.append('sections', sections.join(','));
      body.append('sections_url', window.location.pathname);

      var url = (window.routes && window.routes.cart_add_url) || '/cart/add';
      return fetch(url, {
        method: 'POST',
        body: body,
        headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' },
      })
        .then(function (r) { return r.json(); })
        .then(function (response) {
          if (!response || response.status) {
            status(card, (response && (response.description || response.message)) || 'Δεν προστέθηκε — δοκίμασε ξανά.', 'error');
            track(card, 'error');
            if (btn) btn.removeAttribute('aria-busy');
            return false;
          }
          track(card, 'added', { lp_personalised: text.trim() ? 1 : 0 });
          status(card, 'Προστέθηκε ✓', 'ok');
          try {
            if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
              window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'lp-companion', productVariantId: String(vid), cartData: response });
            }
          } catch (e) { /* a listener's failure is not ours */ }
          paint(card, place, response);
          return true;
        })
        .catch(function () {
          status(card, 'Δεν προστέθηκε — έλεγξε τη σύνδεση και δοκίμασε ξανά.', 'error');
          track(card, 'error');
          if (btn) btn.removeAttribute('aria-busy');
          return false;
        });
    });
  }

  // Is this card inside a drawer that is shut? Dawn opens `cart-drawer` by
  // putting `active` on it. A card in a shut drawer is not on anyone's screen:
  // it must not draw a cover, must not upload a capture, and must not be
  // counted as shown — on 2026-09-24 it was doing all three on every page view
  // with an eligible book in the cart.
  function hiddenInClosedDrawer(card) {
    var drawer = card.closest && card.closest('cart-drawer');
    if (!drawer) return false;
    return !drawer.classList.contains('active');
  }

  // Wait for the drawer to open, then run `go` once.
  function whenDrawerOpens(card, go) {
    var drawer = card.closest && card.closest('cart-drawer');
    if (!drawer || !window.MutationObserver) return;
    var obs = new MutationObserver(function () {
      if (!drawer.classList.contains('active')) return;
      obs.disconnect();
      if (!document.body.contains(card)) return;   // replaced by a re-render
      go();
    });
    obs.observe(drawer, { attributes: true, attributeFilter: ['class'] });
  }

  function init(card) {
    if (card.__lpCompanion) return;

    var place = card.getAttribute('data-lp-place') || 'notification';

    // The cart page renders its own card AND carries the (closed) drawer with
    // one inside. The page's own is the one the customer sees; the drawer's
    // goes, whichever arrives first.
    if (place === 'drawer' && document.querySelector('[data-lp-companion][data-lp-place="cart"]')) {
      card.remove();
      return;
    }
    if (place === 'cart') {
      document.querySelectorAll('[data-lp-companion][data-lp-place="drawer"]').forEach(function (d) { d.remove(); });
    }

    // One card per cart page or drawer: the snippet renders one per eligible
    // item, the first stays (choice overload is the one thing every source
    // agrees on).
    if (place === 'cart' || place === 'drawer') {
      var cards = document.querySelectorAll('[data-lp-companion][data-lp-place="' + place + '"]');
      if (cards.length && cards[0] !== card) { card.remove(); return; }
    }

    // A shut drawer is not a screen: come back when it opens.
    if (hiddenInClosedDrawer(card)) {
      whenDrawerOpens(card, function () { init(card); });
      return;
    }
    card.__lpCompanion = true;

    var variants = parseJson(card.getAttribute('data-lp-variants') || '[]', []);
    card.__lpVariants = variants;
    var picked = pickVariant(variants, card.getAttribute('data-lp-source-variant'));
    var rule = card.getAttribute('data-lp-rule') || 'copy';
    var desc = parseJson(card.getAttribute('data-lp-source-desc') || '', null);
    var layer = desc && desc.layers && desc.layers[0];
    var sourceText = (layer && layer.content) || '';
    var text = deriveText(rule, sourceText, [
      card.getAttribute('data-lp-source-title') || '',
      card.getAttribute('data-lp-source-short') || '',
      card.getAttribute('data-lp-product-title') || '',
      card.getAttribute('data-lp-cover-title') || '',
    ], card.getAttribute('data-lp-cover-title') || '');
    var personalisable = card.getAttribute('data-lp-personalisable') === '1';

    setupSwatches(card, variants, picked && picked.variant, !!(picked && picked.same));

    card.addEventListener('click', function (e) {
      var sw = e.target.closest && e.target.closest('[data-lp-swatch]');
      if (sw && card.contains(sw)) {
        var id = Number(sw.getAttribute('data-lp-swatch'));
        var v = variants.filter(function (x) { return x.id === id; })[0];
        chooseVariant(card, v, true);
        return;
      }
      if (e.target.closest && e.target.closest('[data-lp-companion-skip]')) {
        track(card, 'dismissed');
        card.remove();
        return;
      }
      if (e.target.closest && e.target.closest('[data-lp-companion-add]')) {
        track(card, 'tapped');
        add(card);
      }
    });

    if (!personalisable) {
      if (picked) chooseVariant(card, picked.variant, false);
      track(card, 'shown', { lp_personalised: 0 });
      return;
    }

    ensureElement(card).then(function () {
      var lp = card.querySelector('live-preview');
      if (!lp || !lp.input) { track(card, 'shown', { lp_personalised: 0 }); return; }
      if (picked) chooseVariant(card, picked.variant, false);
      // The source's face and alignment, when the companion offers them —
      // «the same cover» is also the same hand.
      var fam = layer && layer.font && layer.font.family;
      if (text && fam && lp.fontUrls && lp.fontUrls[fam] && typeof lp.onFontChange === 'function') {
        lp.state.font.family = fam;
        var spec = lp.fontSpecs && lp.fontSpecs[fam];
        if (spec) {
          if (spec.sizePct) lp.state.font.sizePct = Number(spec.sizePct);
          if (spec.leading) lp.state.font.leading = Number(spec.leading);
        }
      }
      if (text && layer && layer.font && layer.font.align) lp.state.font.align = layer.font.align;
      lp.input.value = text;
      // Through the element's own door, so the fit, the descriptor and the
      // capture all run — a value set on the field alone draws nothing.
      try { lp.syncFromInput(); } catch (e) { /* never break the page */ }
      // The picture, early: the customer looks before they tap, and the
      // debounce inside the element does the rest.
      var addBtn = card.querySelector('[data-lp-companion-add]');
      if (addBtn && typeof lp.captureNow === 'function') {
        addBtn.addEventListener('pointerdown', function () { try { lp.captureNow(); } catch (e) { /* same */ } }, { passive: true });
      }
      fitField(lp.input);
      showPrice(card);
      lp.input.addEventListener('input', function () {
        fitField(lp.input);
        showPrice(card);
        if (card.__lpEdited) return;
        card.__lpEdited = true;
        track(card, 'edited');
      });
      track(card, 'shown', { lp_personalised: text ? 1 : 0 });
    });
  }

  function scan(root) {
    var nodes = (root && root.querySelectorAll) ? root.querySelectorAll('[data-lp-companion]') : [];
    for (var i = 0; i < nodes.length; i++) init(nodes[i]);
    if (root && root.matches && root.matches('[data-lp-companion]')) init(root);
  }

  function boot() {
    var start = function () {
      scan(document);
      if (window.MutationObserver) {
        new MutationObserver(function (muts) {
          for (var i = 0; i < muts.length; i++) {
            var added = muts[i].addedNodes;
            for (var k = 0; k < added.length; k++) if (added[k].nodeType === 1) scan(added[k]);
          }
        }).observe(document.body, { childList: true, subtree: true });
      }
    };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
    else start();
  }

  return {
    boot: boot,
    init: init,
    // the rules, for tests
    deriveText: deriveText,
    stripPhrases: stripPhrases,
    pickVariant: pickVariant,
    swapRole: swapRole,
    dropTitleLines: dropTitleLines,
    norm: norm,
    ROLE: ROLE,
  };
});
